import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { withConflictConfirm } from '../availability/conflicts';
import { packlistKey } from '../packlists/types';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { Notice } from '../ui/Notice';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import './inputlists.css';
import { dragCount, GEAR, INPUTLISTS_RESOURCE, inputlistKey, isSubstitute, riderHidden, sendPlanText, type Channel, type Gear, type InputlistView, type SendPlan, type SendResult } from './types';

type Patch = Partial<Omit<Channel, 'id' | 'version' | 'micItemName' | 'micRequiresHolder' | 'standItemName'>>;
/** `holderFor`: the row's source — the „Statyw / uchwyt” picker puts the holders learned for that name first. */
type Search = (q: string, signal: AbortSignal, holderFor?: string) => Promise<ItemOption[]>;
type ItemOption = PickerOption & { id: string };

/** Message of a refused write: the field problems, or the server's message. */
const errorText = (e: ApiError) => fieldIssues(e).map((i) => i.message).join(' ') || e.message;

/**
 * Tab „Inputlista” of the event card (owner 2026-11-04): one row per channel — „Kanał”, „Źródło”, „Rider” (what the rider
 * says, free text; the group is hidden while empty), „Dajemy” (catalog items; a yellow „zamiennik” when it does not match
 * the rider), „Uwagi”. Edited in place; „Wyślij do sprzętówki” is manual only and asks with the plan first.
 */
export function InputlistTab({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [showRider, setShowRider] = useState(false);
  const [plan, setPlan] = useState<SendPlan | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // per row: a refused value goes back to the saved one in that row only (review M3)
  const [epochs, setEpochs] = useState<Record<string, number>>({});
  const key = inputlistKey(eventId);
  const list = useQuery({ queryKey: key, queryFn: () => api<InputlistView>(`/api/inputlists/events/${eventId}`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const replace = (c: Channel) =>
    queryClient.setQueryData<InputlistView>(key, (v) => (v ? { ...v, channels: [...v.channels.filter((x) => x.id !== c.id), c].sort((a, b) => a.number - b.number) } : v));

  const add = useMutation({
    mutationFn: () => api<Channel>(`/api/inputlists/events/${eventId}/channels`, { method: 'POST', body: {} }),
    onSuccess: replace,
  });
  const update = useMutation({
    // review M4: saves go one after the other, each with the row's version as the previous save left it
    scope: { id: `inputlist-${eventId}` },
    mutationFn: ({ id, patch }: { id: string; patch: Patch }) => {
      const c = queryClient.getQueryData<InputlistView>(key)?.channels.find((x) => x.id === id);
      return api<Channel>(`/api/inputlists/channels/${id}`, { method: 'PATCH', body: { ...patch, version: c?.version ?? 0 } });
    },
    onSuccess: replace,
    // a refused value (e.g. a number already on the list) goes back to the saved one
    onError: (_e, { id }) => setEpochs((e) => ({ ...e, [id]: (e[id] ?? 0) + 1 })),
  });
  const fill = useMutation({
    // drag down: after the row's pending saves, with its version as they left it; the answer is the whole list
    scope: { id: `inputlist-${eventId}` },
    mutationFn: ({ id, count }: { id: string; count: number }) => {
      const c = queryClient.getQueryData<InputlistView>(key)?.channels.find((x) => x.id === id);
      return api<{ channels: Channel[] }>(`/api/inputlists/channels/${id}/fill`, { method: 'POST', body: { count, version: c?.version ?? 0 } });
    },
    onSuccess: ({ channels }) => queryClient.setQueryData<InputlistView>(key, (v) => (v ? { ...v, channels } : v)),
  });
  const remove = useMutation({
    mutationFn: (c: Channel) => api<void>(`/api/inputlists/channels/${c.id}?version=${c.version}`, { method: 'DELETE' }),
    onSuccess: (_d, c) => queryClient.setQueryData<InputlistView>(key, (v) => (v ? { ...v, channels: v.channels.filter((x) => x.id !== c.id) } : v)),
  });
  const preview = useMutation({
    mutationFn: () => api<SendPlan>(`/api/inputlists/events/${eventId}/send-plan`),
    onSuccess: setPlan,
  });
  const send = useMutation({
    // gear other events reserved first: „Dodaj mimo to” / „Anuluj”, as in the sprzętówka; `expected`: the plan the
    // person saw (review M1: a plan changed meanwhile comes back as 409 `plan_changed` and is shown again)
    mutationFn: (expected: SendPlan) =>
      withConflictConfirm((confirmConflicts) => api<SendResult>(`/api/inputlists/events/${eventId}/send`, { method: 'POST', body: { expected, ...(confirmConflicts ? { confirmConflicts } : {}) } })),
    onSuccess: async (done) => {
      if (!done) return;
      setPlan(null);
      // review I2: what could not be taken back is said, not hidden
      setNotice(['Wysłano do sprzętówki.', ...done.notes].join(' '));
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.code === 'plan_changed' && e.current) setPlan(e.current as SendPlan);
    },
  });

  if (!me) return null;
  const mayAdd = can(me, INPUTLISTS_RESOURCE, 'create');
  const mayEdit = can(me, INPUTLISTS_RESOURCE, 'edit');
  const channels = list.data?.channels ?? [];
  const riderShown = showRider || !riderHidden(channels);
  const writeError = [add.error, update.error, fill.error, remove.error, preview.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const resetWrites = () => [add, update, fill, remove, preview].forEach((m) => m.reset());

  async function search(q: string, signal: AbortSignal, holderFor?: string): Promise<ItemOption[]> {
    const holder = holderFor !== undefined ? `&holderFor=${encodeURIComponent(holderFor)}` : '';
    const res = await api<{ items: Array<{ id: string; name: string; categoryName: string | null; chosenCount?: number | null }> }>(`/api/inputlists/events/${eventId}/suggest?q=${encodeURIComponent(q)}${holder}`, { signal });
    return res.items.map((i) => ({ key: i.id, id: i.id, name: i.name, details: [i.categoryName ?? 'bez kategorii', ...(i.chosenCount ? [`wybrane ${i.chosenCount}×`] : [])] }));
  }

  return (
    <div role="tabpanel" aria-label="Inputlista" className="il-tab" data-feature-id="inputlists.edit">
      <div className="il-head">
        {mayAdd && (
          <button type="button" className="btn small" disabled={add.isPending} onClick={() => add.mutate()}>
            + Dodaj kanał
          </button>
        )}
        {list.data?.canSend && (
          <button type="button" className="btn small primary" data-feature-id="inputlists.send" disabled={preview.isPending || send.isPending} onClick={() => preview.mutate()}>
            Wyślij do sprzętówki
          </button>
        )}
        {!riderShown && mayEdit && (
          <button type="button" className="link-button il-rider-link" onClick={() => setShowRider(true)}>
            pokaż kolumny Rider
          </button>
        )}
      </div>
      {notice && (
        <Notice resetKey={notice} onClose={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać inputlisty'}</p>}
      {writeError?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            resetWrites();
            void refresh();
          }}
        />
      )}
      {writeError && writeError.status !== 409 && (
        <p className="alert" role="alert">
          {errorText(writeError)}
        </p>
      )}
      {list.data && channels.length === 0 && <p className="muted small">Inputlista jest pusta. Dodaj kanały: numer, źródło, co chce rider i co dajemy.</p>}
      {channels.length > 0 && (
        <div className="table-wrap">
          <table className="table il-table">
            <thead>
              <tr>
                <th rowSpan={2} className="num">Kanał</th>
                <th rowSpan={2}>Źródło</th>
                {riderShown && <th colSpan={GEAR.length} className="il-group">Rider</th>}
                <th colSpan={GEAR.length} className="il-group">Dajemy</th>
                <th rowSpan={2}>Uwagi</th>
                {mayEdit && <th rowSpan={2}><span className="visually-hidden">Akcje</span></th>}
              </tr>
              <tr>
                {riderShown && GEAR.map((g) => <th key={`r-${g.key}`}>{g.label}</th>)}
                {GEAR.map((g) => (
                  <th key={`d-${g.key}`}>{g.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <ChannelRow
                  key={c.id}
                  epoch={epochs[c.id] ?? 0}
                  channel={c}
                  riderShown={riderShown}
                  editable={mayEdit}
                  mayFill={mayEdit && mayAdd}
                  busy={update.isPending || remove.isPending}
                  search={search}
                  onChange={(patch) => update.mutate({ id: c.id, patch })}
                  onFill={(count) => fill.mutate({ id: c.id, count })}
                  onDelete={() => remove.mutate(c)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {plan && <SendDialog plan={plan} busy={send.isPending} error={send.error instanceof ApiError ? errorText(send.error) : null} onSend={() => send.mutate(plan)} onClose={() => { setPlan(null); send.reset(); }} />}
    </div>
  );
}

/** A text cell saved when the field is left (Enter or Tab), only when it changed. */
function TextCell({ value, label, editable, onSave, epoch, type = 'text', className }: { value: string; label: string; editable: boolean; onSave: (v: string) => void; epoch: number; type?: 'text' | 'number'; className?: string }) {
  if (!editable) return <span className={className}>{value}</span>;
  const commit = (v: string) => {
    if (v.trim() !== value) onSave(v.trim());
  };
  return (
    <input
      // a new value from the server (a save, a reload) shows; other rows' saves leave a field being typed in alone
      key={`${epoch}:${value}`}
      type={type}
      className={`il-input${className ? ` ${className}` : ''}`}
      aria-label={label}
      defaultValue={value}
      {...(type === 'number' ? { min: 1, max: 999, inputMode: 'numeric' as const } : { maxLength: label.startsWith('Uwagi') ? 1000 : 200 })}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

function ChannelRow({
  channel: c,
  epoch,
  riderShown,
  editable,
  mayFill,
  busy,
  search,
  onChange,
  onFill,
  onDelete,
}: {
  channel: Channel;
  epoch: number;
  riderShown: boolean;
  editable: boolean;
  mayFill: boolean;
  busy: boolean;
  search: Search;
  onChange: (patch: Patch) => void;
  onFill: (count: number) => void;
  onDelete: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const name = `kanał ${c.number}`;
  return (
    <tr>
      <td className="num il-number">
        <TextCell
          epoch={epoch}
          type="number"
          value={String(c.number)}
          label={`Numer: ${name}`}
          editable={editable}
          onSave={(v) => {
            const n = Number(v);
            if (Number.isInteger(n) && n !== c.number) onChange({ number: n });
          }}
        />
      </td>
      <td className="il-source">
        <TextCell epoch={epoch} value={c.source} label={`Źródło: ${name}`} editable={editable} onSave={(source) => onChange({ source })} />
        {mayFill && <FillHandle onFill={onFill} />}
      </td>
      {riderShown &&
        GEAR.map((g) => (
          <td key={`r-${g.key}`} className="il-rider">
            <TextCell epoch={epoch} value={c[g.rider]} label={`Rider ${g.label}: ${name}`} editable={editable} onSave={(v) => onChange({ [g.rider]: v })} />
          </td>
        ))}
      {GEAR.map((g) => (
        <td key={`d-${g.key}`} className="il-give">
          <GiveCell channel={c} gear={g} editable={editable} search={search} onChange={onChange} />
        </td>
      ))}
      <td>
        <TextCell epoch={epoch} value={c.notes} label={`Uwagi: ${name}`} editable={editable} onSave={(notes) => onChange({ notes })} />
      </td>
      {editable && (
        <td className="il-actions">
          {asking ? (
            <span className="il-ask">
              Usunąć?{' '}
              <button type="button" className="btn small" disabled={busy} onClick={onDelete}>
                Tak, usuń
              </button>{' '}
              <button type="button" className="btn small ghost" onClick={() => setAsking(false)}>
                Nie
              </button>
            </span>
          ) : (
            <button type="button" className="btn small ghost" aria-label={`Usuń kanał ${c.number}`} onClick={() => setAsking(true)}>
              Usuń
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * Drag down (owner 2026-11-05): a small handle at the bottom edge of „Źródło” (shown on hover); dragged down over N rows it
 * shows „+N”, and on release asks for N copies of the row right below it. Mouse / pen / touch through pointer events.
 */
function FillHandle({ onFill }: { onFill: (count: number) => void }) {
  const start = useRef<{ y: number; rowHeight: number } | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const end = () => {
    start.current = null;
    setCount(null);
  };
  return (
    <span
      className="il-fill-handle"
      title="Przeciągnij w dół, żeby dodać kolejne takie same kanały"
      aria-hidden="true"
      onPointerDown={(e: PointerEvent<HTMLSpanElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const row = e.currentTarget.closest('tr');
        start.current = { y: e.clientY, rowHeight: row?.getBoundingClientRect().height ?? 0 };
        setCount(0);
      }}
      onPointerMove={(e: PointerEvent<HTMLSpanElement>) => {
        if (start.current) setCount(dragCount(e.clientY - start.current.y, start.current.rowHeight));
      }}
      onPointerUp={(e: PointerEvent<HTMLSpanElement>) => {
        const n = start.current ? dragCount(e.clientY - start.current.y, start.current.rowHeight) : 0;
        end();
        if (n > 0) onFill(n);
      }}
      onPointerCancel={end}
    >
      ↓{count !== null && count > 0 && <span className="il-fill-count">+{count}</span>}
    </span>
  );
}

/**
 * A „Dajemy” cell: the picked catalog item (× clears it) with „zamiennik” when it does not match the rider, or the picker.
 * „Statyw / uchwyt”: the picker puts the holders learned for the row's name first; a red „wymagany uchwyt” while the
 * „Mikrofon / DI” item needs one and none is picked.
 */
function GiveCell({ channel: c, gear: g, editable, search, onChange }: { channel: Channel; gear: Gear; editable: boolean; search: Search; onChange: (patch: Patch) => void }) {
  const itemId = c[g.item];
  const itemName = c[g.name];
  const label = `Dajemy ${g.label}: kanał ${c.number}`;
  const required = g.key === 'stand' && c.micRequiresHolder && c.micItemId !== null && !itemId;
  if (itemId) {
    return (
      <span className="il-picked">
        <span>{itemName ?? 'Pozycja usunięta z katalogu'}</span>
        {isSubstitute(c[g.rider], itemName) && (
          <span className="quote-flag il-substitute" title={`Rider: ${c[g.rider]}`}>
            zamiennik
          </span>
        )}
        {editable && (
          <button type="button" className="btn small ghost il-clear" aria-label={`Wyczyść: ${label}`} onClick={() => onChange({ [g.item]: null })}>
            ×
          </button>
        )}
      </span>
    );
  }
  const marker = required && (
    <span className="quote-flag il-required" title="Ten mikrofon / DI wymaga uchwytu (pole „Wymaga uchwytu” w katalogu)">
      wymagany uchwyt
    </span>
  );
  if (!editable) return marker || null;
  return (
    <span className="il-pick">
      <QuickPicker<ItemOption>
        label={label}
        labelHidden
        placeholder="wybierz…"
        search={(q, signal) => search(q, signal, g.key === 'stand' ? c.source : undefined)}
        onPick={(o) => onChange({ [g.item]: o.id })}
      />
      {marker}
    </span>
  );
}

function SendDialog({ plan, busy, error, onSend, onClose }: { plan: SendPlan; busy: boolean; error: string | null; onSend: () => void; onClose: () => void }) {
  const empty = plan.add.length === 0 && plan.remove.length === 0;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="il-send-title"
        data-feature-id="inputlists.send"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="il-send-title">Wyślij do sprzętówki</h2>
        <ul className="il-plan">
          {sendPlanText(plan).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {!empty && <p className="muted small">Sprzętu dodanego w sprzętówce ręcznie nie zabiorę.</p>}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          {!empty && (
            <button type="button" className="btn primary" disabled={busy} onClick={onSend} autoFocus>
              Wyślij
            </button>
          )}
          <button type="button" className="btn" disabled={busy} onClick={onClose} {...(empty ? { autoFocus: true } : {})}>
            {empty ? 'Zamknij' : 'Anuluj'}
          </button>
        </div>
      </div>
    </div>
  );
}
